import React from 'react';
import { X, Music, Radio, Wind, Sparkles, Waves, Zap, Sliders, ShieldCheck } from 'lucide-react';

interface AcousticLegendModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AcousticLegendModal: React.FC<AcousticLegendModalProps> = ({
  isOpen,
  onClose
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md">
      <div className="max-w-2xl w-full glass-panel p-6 rounded-3xl border border-slate-700 shadow-2xl max-h-[85vh] overflow-y-auto space-y-6">
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-cyan-500/20 text-cyan-400">
              <Music className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Acoustic Mapping & Musical Rulebook</h2>
              <p className="text-xs text-slate-400">Core Audio Architecture, Scale Quantization & Synth Patches</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Anchor & Musical Mode Explanation */}
        <div className="p-4 rounded-2xl bg-gradient-to-r from-indigo-950/60 to-purple-950/60 border border-purple-800/60 space-y-2">
          <h3 className="text-sm font-bold text-purple-300 flex items-center gap-2">
            <Music className="w-4 h-4 text-purple-400" />
            <span>Musical Sonification Mode & Modal Transitions</span>
          </h3>
          <p className="text-xs text-slate-300 leading-relaxed">
            <strong className="text-white">Scientific vs. Musical Mode:</strong> Users can toggle between microtonal linear pitch tracking and musical scale quantization in the top header bar.<br />
            <strong className="text-white">Modal Climate Transitions:</strong> As global warming increases, the musical scale shifts dynamically:
          </p>
          <ul className="text-xs space-y-1 list-disc list-inside text-slate-300 pt-1">
            <li><strong className="text-cyan-300">0.0°C to +0.8°C:</strong> C Lydian Mode (bright, stable, serene baseline)</li>
            <li><strong className="text-amber-300">+0.8°C to +1.8°C:</strong> C Mixolydian Mode (suspensive, mild tension)</li>
            <li><strong className="text-orange-300">+1.8°C to +2.8°C:</strong> C Dorian Mode (somber, serious minor)</li>
            <li><strong className="text-red-300">+2.8°C to +5.0°C:</strong> C Locrian / Diminished Mode (severe dissonance, unresolved collapse)</li>
          </ul>
        </div>

        {/* Pitch Anchor */}
        <div className="p-4 rounded-2xl bg-cyan-950/40 border border-cyan-800/60 space-y-2">
          <h3 className="text-sm font-bold text-cyan-300 flex items-center gap-2">
            <Radio className="w-4 h-4" />
            <span>Thermal Pitch Anchor & Mapping</span>
          </h3>
          <p className="text-xs text-slate-300 leading-relaxed">
            <strong className="text-white">0.0°C Baseline:</strong> Mapped to Middle C (261.63 Hz / C4).<br />
            <strong className="text-white">Thermal Scaling:</strong> Temperature anomalies map up to 1046.50 Hz (C6 at +5.0°C).
          </p>
        </div>

        {/* The 6 Synthesizer Patches */}
        <div className="space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">
            Procedural Synthesizer Engine Patches
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div className="p-3.5 rounded-xl bg-slate-900/90 border border-slate-800 space-y-1">
              <div className="flex items-center gap-2 text-amber-400 font-semibold">
                <Music className="w-4 h-4" />
                <span>1. Nat-Warmth</span>
              </div>
              <p className="text-slate-400">
                Subtractive dual-triangle synth + organic LFO breathing (0.2 Hz) for natural background baseline.
              </p>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-900/90 border border-slate-800 space-y-1">
              <div className="flex items-center gap-2 text-cyan-400 font-semibold">
                <Radio className="w-4 h-4" />
                <span>2. Anthropo-Pulse</span>
              </div>
              <p className="text-slate-400">
                Inharmonic FM synth + resonant formant filter sweep sonifying CO2 emissions & human industrial drivers.
              </p>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-900/90 border border-slate-800 space-y-1">
              <div className="flex items-center gap-2 text-teal-400 font-semibold">
                <Wind className="w-4 h-4" />
                <span>3. Aerosol-Shield</span>
              </div>
              <p className="text-slate-400">
                Dual staggered bandpass noise filters (Golden Ratio offset) sonifying aerosol cooling scattering.
              </p>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-900/90 border border-slate-800 space-y-1">
              <div className="flex items-center gap-2 text-blue-400 font-semibold">
                <Sparkles className="w-4 h-4" />
                <span>4. Cryo-Hiss</span>
              </div>
              <p className="text-slate-400">
                5-bit quantization WaveShaper + high-pass noise for sea ice brittleness and ocean acidification.
              </p>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-900/90 border border-slate-800 space-y-1">
              <div className="flex items-center gap-2 text-indigo-400 font-semibold">
                <Waves className="w-4 h-4" />
                <span>5. Abyssal-Drone</span>
              </div>
              <p className="text-slate-400">
                Sub-bass sine synth + Tanh saturation (phantom harmonics audible on laptop speakers) for sea level rise.
              </p>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-900/90 border border-slate-800 space-y-1">
              <div className="flex items-center gap-2 text-red-400 font-semibold">
                <Zap className="w-4 h-4" />
                <span>6. Extreme-Transient</span>
              </div>
              <p className="text-slate-400">
                Multi-layered transient generator (sawtooth drop + noise impact thump) for extreme weather hits.
              </p>
            </div>
          </div>
        </div>

        <div className="pt-2 text-right">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs shadow-lg shadow-cyan-500/20"
          >
            Got It
          </button>
        </div>
      </div>
    </div>
  );
};
